import React, { useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { HiInformationCircle } from "react-icons/hi";
import { MdArrowForward, MdOutlineEventAvailable } from "react-icons/md";

import useAuth from "../../../../../hooks/useAuth";
import {
  useGetAgendaConfig,
  useGetAgendaTypes,
} from "../../../../../hooks/agenda";
import { Surface } from "../../../../atoms/Surface";
import { AlertMessage } from "../../../../atoms/AlertMessage";
import { CustomButton } from "../../../../atoms/CustomButton";
import { EmptyState } from "../../../../atoms/EmptyState";
import { Spinner } from "../../../../atoms/Spinner";
import { Switch } from "../../../../atoms/Switch";
import NoTranslate from "../../../../atoms/NoTranslate";
import {
  AGENDA_PATH,
  AGENDA_TYPES_PATH,
  agendaTypePath,
  useFormatDuration,
} from "../../agenda/agendaTypes";
import { formatPrice } from "../../agenda/agendaPayment";
import type { RegistrationFormData } from "../types";

type Props = {
  control: Control<RegistrationFormData>;
  errors: FieldErrors<RegistrationFormData>;
  readOnly?: boolean;
};

const fieldClass = (hasError: boolean) =>
  twMerge(
    "block w-full rounded-md border bg-white p-2.5 pr-8 text-base text-gray-900 shadow-sm md:py-2 md:text-sm disabled:cursor-not-allowed disabled:opacity-50",
    hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
  );

export const BookingSection: React.FC<Props> = ({
  control,
  errors,
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const formatDuration = useFormatDuration();
  const { user, customer_identification } = useAuth();
  const entityIdentification = user?.entity_identification || "";

  const {
    data: agendaConfig,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useGetAgendaConfig(customer_identification, entityIdentification);
  const agendaEnabled = agendaConfig?.enabled === true;

  const {
    data: types = [],
    isLoading: isLoadingTypes,
    isError: isTypesError,
  } = useGetAgendaTypes(customer_identification, entityIdentification, {
    enabled: agendaEnabled,
  });

  const bookingEnabled = useWatch({ control, name: "booking.enabled" });
  const typeId = useWatch({ control, name: "booking.type_id" });

  const activeTypes = useMemo(
    () => types.filter((type) => type.active),
    [types]
  );
  const selectedType = types.find((type) => type.id === typeId);
  const isLoading = isLoadingConfig || (agendaEnabled && isLoadingTypes);
  const isError = isConfigError || isTypesError;
  const isReady = agendaEnabled && activeTypes.length > 0;

  const typeError = errors?.booking?.type_id;

  return (
    <Surface>
      <div className="mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <MdOutlineEventAvailable size={22} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-gray-900 text-base font-semibold mb-1">
              {t("customRegistration.booking.title")}
            </h3>
            <p className="text-gray-500 text-sm leading-tight">
              {t("customRegistration.booking.description")}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Controller
          name="booking.enabled"
          control={control}
          render={({ field }) => (
            <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
              <label
                htmlFor="booking-toggle"
                className="cursor-pointer text-sm font-medium text-gray-900"
              >
                {t("customRegistration.booking.enableLabel")}
              </label>
              <Switch
                id="booking-toggle"
                checked={field.value || false}
                disabled={readOnly || isLoading || (!isReady && !field.value)}
                onChange={field.onChange}
              />
            </div>
          )}
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-6" translate="no">
          <Spinner size="sm" />
        </div>
      )}

      {isError && (
        <AlertMessage color="failure" icon={HiInformationCircle}>
          {t("customRegistration.booking.loadError")}
        </AlertMessage>
      )}

      {!isLoading && !isError && !isReady && (
        <EmptyState
          bordered
          icon={<MdOutlineEventAvailable size={24} />}
          title={
            agendaEnabled
              ? t("customRegistration.booking.noTypes.title")
              : t("customRegistration.booking.agendaOff.title")
          }
          description={
            agendaEnabled
              ? t("customRegistration.booking.noTypes.description")
              : t("customRegistration.booking.agendaOff.description")
          }
          action={
            <CustomButton
              variant="outline"
              onClick={() =>
                navigate(agendaEnabled ? AGENDA_TYPES_PATH : AGENDA_PATH)
              }
              title={
                (agendaEnabled
                  ? t("customRegistration.booking.noTypes.action")
                  : t("customRegistration.booking.openAgenda")) as string
              }
              icon={<MdArrowForward size={16} className="rtl:rotate-180" />}
            />
          }
        />
      )}

      {bookingEnabled && !isReady && typeError && (
        <p className="mt-2 text-sm font-medium text-red-600">
          {typeError.message}
        </p>
      )}

      {bookingEnabled && isReady && (
        <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 animate-slideIn">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
            <div className="min-w-0 flex-1 sm:basis-64">
              <label
                htmlFor="booking-type"
                className="block text-base font-semibold text-gray-900"
              >
                {t("customRegistration.booking.type.label")}
              </label>
              <p className="mt-1 text-xs text-gray-600">
                {t("customRegistration.booking.type.helper")}
              </p>
            </div>
            <Link
              to={
                selectedType
                  ? agendaTypePath(selectedType.id)
                  : AGENDA_TYPES_PATH
              }
              className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-blue-700 hover:text-blue-800"
            >
              <MdArrowForward size={14} className="rtl:rotate-180" />
              {selectedType
                ? t("customRegistration.booking.type.edit")
                : t("customRegistration.booking.type.manage")}
            </Link>
          </div>

          <Controller
            name="booking.type_id"
            control={control}
            render={({ field }) => (
              <select
                id="booking-type"
                value={field.value || ""}
                disabled={readOnly}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                className={fieldClass(!!typeError)}
              >
                <option value="" disabled>
                  {t("customRegistration.booking.type.placeholder")}
                </option>
                {selectedType && !selectedType.active && (
                  <option value={selectedType.id}>{selectedType.title}</option>
                )}
                {activeTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.title}
                  </option>
                ))}
              </select>
            )}
          />
          {typeError && (
            <p className="text-sm font-medium text-red-600">
              {typeError.message}
            </p>
          )}

          {typeId && !selectedType && (
            <AlertMessage color="warning">
              {t("customRegistration.booking.type.missing")}
            </AlertMessage>
          )}
          {selectedType && !selectedType.active && (
            <AlertMessage color="warning">
              {t("customRegistration.booking.type.inactive")}
            </AlertMessage>
          )}

          {selectedType && (
            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-gray-500">
                {t("customRegistration.booking.summary.duration")}
              </dt>
              <dd className="text-gray-700">
                {formatDuration(selectedType.duration_min)}
              </dd>
              <dt className="text-gray-500">
                {t("customRegistration.booking.summary.price")}
              </dt>
              <dd className="tabular-nums text-gray-700">
                {selectedType.payment
                  ? formatPrice(
                      selectedType.payment.amount,
                      selectedType.payment.currency,
                      i18n.language
                    )
                  : t("customRegistration.booking.summary.free")}
              </dd>
              <dt className="text-gray-500">
                {t("customRegistration.booking.summary.members")}
              </dt>
              <dd className="min-w-0 truncate text-gray-700">
                <NoTranslate>
                  {selectedType.members.map((m) => m.name).join(", ")}
                </NoTranslate>
              </dd>
              <dt className="text-gray-500">
                {t("customRegistration.booking.summary.notice")}
              </dt>
              <dd className="text-gray-700">
                {t("customRegistration.booking.summary.noticeValue", {
                  count: selectedType.min_notice_hours,
                })}
              </dd>
              <dt className="text-gray-500">
                {t("customRegistration.booking.summary.horizon")}
              </dt>
              <dd className="text-gray-700">
                {t("customRegistration.booking.summary.horizonValue", {
                  count: selectedType.horizon_days,
                })}
              </dd>
            </dl>
          )}
        </section>
      )}
    </Surface>
  );
};

export default BookingSection;
